import {
  ReactNode,
  useEffect,
  useLayoutEffect,
  useContext,
  useMemo,
} from 'react'
import { RouteComponentProps } from 'react-router-dom'
import { useIntl } from 'react-intl'

import type { HomeId } from '@racepointenergy/swc/scs/types'
import { notAvailableHostMessages } from 'features/home/components/HomeStatus/MainInfo/messages'
import { noMonitoringAccessMessages } from 'features/home/components/HomeConfiguration/messages'
import { useAppDispatch, useAppSelector } from 'app/hooks'

import { hasSysMonSupport } from './utils'
import { sysMonErrorMessages } from './messages'
import { HomeStateContext } from '../homeStateContext'
import { homeConfigSelectors } from 'features/home'
import {
  getHomeConfigThunk,
  homeConfigActions,
} from '../components/HomeConfiguration/slice'

export interface GuardRouterProps
  extends RouteComponentProps<{ homeId: HomeId }> {}

interface AvailabilityCheckerChildren {
  errors: string[]
  isSysMonSupported: boolean
  isAvailabilityLoading: boolean
}

interface AvailabilityCheckerProps {
  homeId: HomeId
  children: ({
    errors,
    isSysMonSupported,
    isAvailabilityLoading,
  }: AvailabilityCheckerChildren) => ReactNode
}

const AvailabilityChecker = ({
  homeId,
  children,
}: AvailabilityCheckerProps) => {
  const dispatch = useAppDispatch()
  const { formatMessage } = useIntl()
  const {
    isOnline,
    hasMonitorAccessEnabled,
    isLoading: isHomeInfoLoading,
  } = useContext(HomeStateContext)

  useLayoutEffect(() => {
    dispatch(homeConfigActions.clear())
  }, [dispatch])

  const hostConfig = useAppSelector(homeConfigSelectors.data)
  const isHostConfigFetching = useAppSelector(homeConfigSelectors.isFetching)
  const hasHostConfigFetched = useAppSelector(homeConfigSelectors.isFinished)
  const isSysMonSupported = useMemo(() => {
    return !!hostConfig && hasSysMonSupport(hostConfig)
  }, [hostConfig])

  useEffect(() => {
    dispatch(getHomeConfigThunk(homeId))
  }, [dispatch, homeId])

  const isAvailabilityLoading = isHostConfigFetching || isHomeInfoLoading

  const errors = useMemo(() => {
    const result = []

    // making sure null/undefined are not treated as offline
    if (isOnline === false) {
      result.push(formatMessage(notAvailableHostMessages.notAvailableHostTitle))
    }

    // making sure null/undefined are not treated as missing access
    if (hasMonitorAccessEnabled === false) {
      result.push(
        formatMessage(noMonitoringAccessMessages.noMonitoringAccesTitle)
      )
    }

    if (hasHostConfigFetched && !isSysMonSupported) {
      result.push(formatMessage(sysMonErrorMessages.oldVersionTitle))
    }

    return result
  }, [
    isOnline,
    hasMonitorAccessEnabled,
    hasHostConfigFetched,
    isSysMonSupported,
    formatMessage,
  ])

  return <>{children({ errors, isSysMonSupported, isAvailabilityLoading })}</>
}

export default AvailabilityChecker
